import { useEffect, useState } from 'react'
import { useLiveQuery } from '@tanstack/react-db'
import { ilike, or } from '@tanstack/db'

import { Search, type SearchOption } from '@/components/ui/Search'
import { useDb } from '@/lib/dbContext'

function useDebouncedValue<T>(value: T, delayMs: number) {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timeoutId)
  }, [value, delayMs])

  return debounced
}

export type AutocompleteCustomerContainerProps = {
  value: SearchOption | null
  onChange: (option: SearchOption | null) => void
  ariaLabel?: string
  lockAfterSelect?: boolean
  allowCreateOption?: boolean
  disabled?: boolean
  limit?: number
  debounceMs?: number
}

export function AutocompleteCustomerContainer(
  props: AutocompleteCustomerContainerProps,
) {
  const { customersCollection } = useDb()
  const {
    value,
    onChange,
    ariaLabel = 'Customer',
    lockAfterSelect,
    allowCreateOption,
    disabled,
    limit = 25,
    debounceMs = 150,
  } = props

  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query.trim(), debounceMs)

  const customersResult = useLiveQuery(
    (q) => {
      const base = q.from({ customer: customersCollection })

      const filtered = debouncedQuery
        ? base.where(({ customer }) =>
            or(
              ilike(customer.fullName, `%${debouncedQuery}%`),
              ilike(customer.externalId, `%${debouncedQuery}%`),
            ),
          )
        : base

      return filtered
        .select(({ customer }) => ({
          id: customer.id,
          fullName: customer.fullName,
          externalId: customer.externalId,
          contactAddress: customer.contactAddress,
          contactEmail: customer.contactEmail,
          contactPhone: customer.contactPhone,
          invoiceName: customer.invoiceName,
          invoiceAddress: customer.invoiceAddress,
          invoiceEmail: customer.invoiceEmail,
          invoicePhone: customer.invoicePhone,
          invoiceVat: customer.invoiceVat,
        }))
        .orderBy(({ customer }) => customer.fullName)
        .limit(limit)
    },
    [debouncedQuery, limit],
  )

  const options = (customersResult.data ?? []).map((customer) => ({
    id: customer.id,
    // The Omni ID is a dropdown-row hint only — composing it into `name`
    // puts it in the input text (and once fed back as the search query it
    // matches nothing server-side).
    name: customer.fullName,
    hint: customer.externalId || undefined,
    fullName: customer.fullName,
    contactAddress: customer.contactAddress,
    contactEmail: customer.contactEmail,
    contactPhone: customer.contactPhone,
    invoiceName: customer.invoiceName,
    invoiceAddress: customer.invoiceAddress,
    invoiceEmail: customer.invoiceEmail,
    invoicePhone: customer.invoicePhone,
    invoiceVat: customer.invoiceVat,
  })) as SearchOption[]

  return (
    <Search
      options={options}
      value={value}
      onChange={onChange}
      ariaLabel={ariaLabel}
      onQueryChange={setQuery}
      isLoading={customersResult.isLoading}
      errorMessage={
        customersResult.isError ? 'Unable to lookup customers' : undefined
      }
      lockAfterSelect={lockAfterSelect}
      allowCreateOption={allowCreateOption}
      disabled={disabled}
    />
  )
}
